import { Spinner } from '@dehaat/dds';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ORDERING_FOR_SELECTED_FARMER } from '@/constants/common';
import useDebounce from '@/hooks/useDebounce';
import addNewFarmer from '@/images/add-new-farmer.webp';
import fetchVrNetwork, { VrNetworkList } from '@/utils/api/fetchVrNetwork';
import { randomGradient } from '@/utils/randomGradient';
import AddNewFarmerWithAddress from '../address/AddNewFarmerWithAddress';
import HeaderWithBackButton from '../HeaderWithBackButton';
import ArrowButton from '../icons/ArrowButton';
import NewSearchBox from '../NewSearchBox';
import FarmerCard from './FarmerCard';
interface Props {
  isDrawerOpen?: boolean;
  isAddFarmerFormVisible?: boolean;
  setShowAddFarmer?: (val: boolean) => void;
  onFarmerSelected?: () => void;
}
const SelectFarmer = ({
  isDrawerOpen = false,
  onFarmerSelected,
  isAddFarmerFormVisible,
  setShowAddFarmer
}: Props) => {
  const {
    t
  } = useTranslation('common');
  const {
    push,
    locale,
    back,
    replace,
    query
  } = useRouter();
  const [selectedFarmer, setSelectFarmer] = useState<VrNetworkList>();
  const [farmerList, setFarmerList] = useState<VrNetworkList[]>([]);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [, setIsTyped] = useState(false);
  const didFetchRef = useRef(false);
  const redirectUri = typeof query.redirect_uri === 'string' ? query.redirect_uri : Array.isArray(query.redirect_uri) ? query.redirect_uri[0] : null;
  const safeRedirectUri = redirectUri && redirectUri.startsWith('/') ? redirectUri : null;
  const gradients = useMemo(() => {
    const map: Record<string, string> = {};
    farmerList.forEach(f => {
      map[f.id] = randomGradient();
    });
    return map;
  }, [farmerList]);
  const getVrNetworkData = async (url: string) => {
    try {
      setIsLoading(true);
      const data = await fetchVrNetwork(url);
      setFarmerList(prev => [...prev, ...data.results]);
      setNextPage(typeof data.next === 'string' ? data.next.replace(/^http:/, 'https:') : data.next);
    } catch (e) {} finally {
      setIsLoading(false);
    }
  };
  const debouncedSearch = useDebounce((newSearchQuery: string) => {
    if (searchQuery !== newSearchQuery) {
      setIsTyped(false);
      setSearchQuery(newSearchQuery);
      setFarmerList([]);
      setNextPage(null);
      getVrNetworkData(`/vr/v2/vr-network?search=${newSearchQuery}`);
    }
  }, 500);
  useEffect(() => {
    const lastChild = document.querySelector('.farmer-card:last-child');
    if (!lastChild) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        observer.unobserve(lastChild);
        if (nextPage) {
          getVrNetworkData(nextPage);
        }
      }
    });
    observer.observe(lastChild);
    return () => {
      if (lastChild) {
        observer.unobserve(lastChild);
      }
      observer.disconnect();
    };
  }, [farmerList, nextPage]);
  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    if (sessionStorage.getItem(ORDERING_FOR_SELECTED_FARMER)) {
      sessionStorage.removeItem(ORDERING_FOR_SELECTED_FARMER);
    }
    setFarmerList([]);
    setNextPage(null);
    getVrNetworkData('/vr/v2/vr-network');
  }, []);
  return <main className="relative flex h-screen" data-sentry-component="SelectFarmer" data-sentry-source-file="SelectFarmer.tsx">
      {!isDrawerOpen && <HeaderWithBackButton heading={t('Select farmer')} showHomeIcon={false} showProfileIcon={false} onBackClick={back} />}
      <section className="relative flex flex-col gap-4 bg-blue-50/50 m-auto px-4 py-20 w-full h-full min-h-screen">
        <NewSearchBox placeholder="Search by name or phone no." lang={locale || DEFAULT_LANG} showBackBtn onFocus={() => window.scrollTo(0, 0)} initialSearchText={searchQuery} setIsTyped={setIsTyped} onSearch={debouncedSearch} data-sentry-element="NewSearchBox" data-sentry-source-file="SelectFarmer.tsx" />
        {isLoading && <div className="z-50 absolute inset-0 flex justify-center items-center bg-black/10">
            <Spinner type="in-screen" />
          </div>}

        <div className="flex flex-col flex-1 gap-2 overflow-auto">
          <p className="font-normal text-neutral-80 text-sm">
            {t('select_farmer_for_ordering')}
          </p>

          {farmerList.length > 0 ? farmerList.map((item, index) => <FarmerCard key={item.id} item={item} isSelected={selectedFarmer?.id === item.id} onSelect={setSelectFarmer} gradient={gradients[item.id]} isLastItem={farmerList.length - 1 === index} />) : <div className="flex flex-1 justify-center items-center">
              <p className="px-4 py-10 font-medium text-neutral-80 text-base text-center">
                {t('farmer_list_is_empty')}
              </p>
            </div>}
        </div>

        <button className="bottom-[5.5rem] left-1/2 absolute flex justify-center items-center gap-2 bg-white py-2 pr-4 pl-2 border border-neutral-20 rounded-[48px] w-[15.5rem] -translate-x-1/2" onClick={() => {
        if (isDrawerOpen) {
          setShowAddFarmer?.(true);
        } else {
          push('/add-new-farmer');
        }
      }}>
          <div className="flex gap-2">
            <Image src={addNewFarmer} alt="AddNewFarmer" width={40} height={40} data-sentry-element="Image" data-sentry-source-file="SelectFarmer.tsx" />
            <div className="flex flex-col">
              <p className="font-normal text-[10px] text-neutral-70 text-left">
                {t('ordering_for_new_farmer')}?
              </p>
              <p className="font-semibold text-primary-100 text-sm text-left">
                {t('add_new_farmer')}
              </p>
            </div>
          </div>
          <ArrowButton width={24} height={24} bgColor={'#0E7A41'} data-sentry-element="ArrowButton" data-sentry-source-file="SelectFarmer.tsx" />
        </button>
        <div className="bottom-0 left-0 fixed bg-white shadow-[0_-2px_6px_rgba(0,0,0,0.16)] px-4 py-3 w-full">
          <button className={`px-8 py-3 rounded-lg w-full ${selectedFarmer ? 'bg-primary-100' : 'bg-neutral-50'}`} onClick={() => {
          if (!selectedFarmer) return;
          sessionStorage.setItem(ORDERING_FOR_SELECTED_FARMER, JSON.stringify(selectedFarmer));
          if (isDrawerOpen) {
            onFarmerSelected?.();
          } else {
            if (query.fs) {
              replace(`/farmer-services/cart`);
            } else if (safeRedirectUri) {
              replace(safeRedirectUri);
            } else {
              replace({
                pathname: '/cart'
              });
            }
          }
        }}>
            <p className="font-semibold text-white text-base">{t('next')}</p>
          </button>
          {isAddFarmerFormVisible && <AddNewFarmerWithAddress isPopup handleSave={() => setShowAddFarmer?.(false)} />}
        </div>
      </section>
    </main>;
};
export default SelectFarmer;